/**
 * `<caos-location-field>` — a place, entered as one latitude and longitude pair. (Catalog: Fields & input › LocationField — enter a place as one latitude and longitude pair that commits together and is refused the way a save refuses it.)
 *
 * The editor primitive behind the `Geolocation` storage kind (CAOS-849). A Geolocation value is
 * ONE value with two named subfields — the kernel stores a single PostGIS point and never exposes
 * the coordinates as separately-writable columns — so this is one control with two boxes, not two
 * number fields placed side by side.
 *
 * THREE RULES IT KEEPS:
 *
 * 1. THE PAIR COMMITS TOGETHER. `change` fires when focus leaves the whole control, or on Enter —
 *    never when focus moves from one box to the other. Two independently-committing boxes would
 *    write a new latitude against the old longitude the moment the first box lost focus: a real
 *    place nobody meant, and a valid one, so nothing downstream would refuse it.
 *
 * 2. EMPTY IS NOT ZERO. `Number('')` is `0`, and 0, 0 is a real position — the null island a
 *    broken import produces. Both boxes empty is "no place"; one box empty is a missing coordinate,
 *    refused under that box.
 *
 * 3. IT REFUSES WHAT A SAVE WOULD REFUSE, IN THE SAVE'S WORDS. The checks are the kernel's
 *    Geolocation write validation (`kernel/src/save/value-validation.ts`): a coordinate that is
 *    missing or not a number, a latitude outside -90 to 90, a longitude outside -180 to 180. The
 *    reason goes under the box that is wrong, and nothing is committed while one is showing. This
 *    is a courtesy to the person typing, not the gate — the save still judges every write.
 *
 * The boxes are text inputs with a decimal keypad rather than number inputs. A number input reports
 * an unparseable entry as an empty string, which would turn "not a number" into "missing", and it
 * refuses a pasted "37.7749, -122.4194" outright — the form a coordinate is usually copied in.
 *
 * Styling seams: `::part(label)`, `::part(control)`, `::part(latitude)`, `::part(longitude)`,
 * `::part(latitude-message)`, `::part(longitude-message)`, `::part(message)`. Fires a bubbling,
 * composed `change` when a different pair (or no place) is committed; `value` reads the pair back.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** A committed place. */
export interface CaosLocationValue {
  readonly latitude: number;
  readonly longitude: number;
}

/** What the two boxes add up to, judged the way a save judges a Geolocation value. */
export interface CaosLocationReading {
  /** The pair; `null` when both boxes are empty; `undefined` when either box is refused. */
  readonly value: CaosLocationValue | null | undefined;
  /** Why the latitude box is refused, or `''`. */
  readonly latitude: string;
  /** Why the longitude box is refused, or `''`. */
  readonly longitude: string;
}

type AxisName = 'latitude' | 'longitude';

interface Axis {
  readonly name: AxisName;
  readonly label: string;
  /** The coordinate's bound either side of zero. */
  readonly limit: number;
}

const AXES: readonly Axis[] = [
  { name: 'latitude', label: 'Latitude', limit: 90 },
  { name: 'longitude', label: 'Longitude', limit: 180 },
];

/** Why one box's text is not a coordinate on its axis, or `''` when it is. */
function refusal(axis: Axis, text: string): string {
  const n = text === '' ? Number.NaN : Number(text);
  if (!Number.isFinite(n)) return `Needs a ${axis.name}, as a number between -${axis.limit} and ${axis.limit}.`;
  if (n < -axis.limit || n > axis.limit) {
    return `${axis.label} ${n} is outside the range -${axis.limit} to ${axis.limit}, so it is not a point on Earth.`;
  }
  return '';
}

/** Judge the text of the two boxes. Exported so the rule can be read, and tested, without a DOM. */
export function readLocation(latitudeText: string, longitudeText: string): CaosLocationReading {
  const lat = latitudeText.trim();
  const lon = longitudeText.trim();
  if (lat === '' && lon === '') return { value: null, latitude: '', longitude: '' };
  const latitude = refusal(AXES[0]!, lat);
  const longitude = refusal(AXES[1]!, lon);
  if (latitude || longitude) return { value: undefined, latitude, longitude };
  return { value: { latitude: Number(lat), longitude: Number(lon) }, latitude: '', longitude: '' };
}

const COORDINATE = '([-+]?(?:\\d+(?:\\.\\d*)?|\\.\\d+))';
const PAIR = new RegExp(`^\\s*\\(?\\s*${COORDINATE}\\s*(?:[,;]\\s*|\\s+)${COORDINATE}\\s*\\)?\\s*$`);

/** "37.7749, -122.4194" (or `;`, a space, or parentheses) as the two boxes' text; `null` otherwise. */
export function splitLocationPair(text: string): [string, string] | null {
  const m = PAIR.exec(text);
  return m ? [m[1]!, m[2]!] : null;
}

function sameLocation(a: CaosLocationValue | null, b: CaosLocationValue | null): boolean {
  if (a === null || b === null) return a === b;
  return a.latitude === b.latitude && a.longitude === b.longitude;
}

const sheet = makeSheet(`
  :host { display: block; }

  .field { display: flex; flex-direction: column; gap: var(--caos-space-1); }

  .label {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-weight: 600;
    color: var(--caos-color-text-muted);
  }
  .label-text:empty { display: none; }
  .req { color: var(--caos-color-danger); margin-left: 2px; }

  .pair { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--caos-space-3); }
  .coord { display: flex; flex-direction: column; gap: var(--caos-space-1); min-width: 0; }

  .sub-label {
    font-family: var(--caos-font);
    font-size: var(--caos-text-label);
    color: var(--caos-color-text-muted);
  }

  input {
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: var(--caos-space-3) 14px;
    border-radius: var(--caos-radius);
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-variant-numeric: tabular-nums;
    line-height: 1.4;
    transition: border-color 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
  }
  input::placeholder { color: var(--caos-color-text-muted); opacity: 0.85; }
  input:hover:not(:disabled):not([readonly]):not(:focus) { border-color: color-mix(in srgb, var(--caos-color-accent) 45%, var(--caos-color-border)); }
  input:focus-visible,
  input:focus {
    outline: none;
    border-color: var(--caos-color-border-strong);
    box-shadow: inset 0 0 0 100px var(--caos-accent-wash);
  }
  input:disabled {
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text-muted);
    cursor: not-allowed;
    opacity: var(--caos-state-disabled-opacity);
  }
  /* Read-only: the place stays in full colour and can be focused, selected and copied — only the
     dashed edge says the boxes do not take typing. Disabled, by contrast, greys out and refuses focus. */
  input[readonly] {
    background: transparent;
    border-style: dashed;
  }

  /* Invalid is per box: a refused latitude does not colour a longitude that is fine. */
  input[aria-invalid='true'] { border-color: var(--caos-color-danger); }
  input[aria-invalid='true']:focus-visible,
  input[aria-invalid='true']:focus {
    border-color: var(--caos-color-danger);
    box-shadow: inset 0 0 0 100px color-mix(in srgb, var(--caos-color-danger) 6%, transparent);
  }

  .sub-message,
  .message {
    font-family: var(--caos-font);
    font-size: 11.5px;
    line-height: 1.4;
  }
  .sub-message { color: var(--caos-color-danger); }
  .message { color: var(--caos-color-text-muted); }
  :host([invalid]) .message { color: var(--caos-color-danger); }
  .sub-message:empty,
  .message:empty { display: none; }

  /* Inline (record-detail) variant: two underlined boxes in the surrounding type. The group label
     and the field message are the record page's to show. Two things stay. The box labels, beside
     their boxes: a record page opens this already holding a place, so a placeholder would never be
     seen and the two numbers would sit there unnamed, order left to guesswork. And the per-box
     refusal, because it is the only thing that says why Enter did not save. */
  :host([inline]) { display: inline-block; width: 100%; }
  :host([inline]) .field { gap: 0; }
  :host([inline]) .label,
  :host([inline]) .message { display: none; }
  :host([inline]) .coord {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: baseline;
    column-gap: var(--caos-space-1);
    row-gap: 0;
  }
  :host([inline]) .sub-message { grid-column: 1 / -1; }
  :host([inline]) input {
    min-height: 0;
    padding: 0 0 2px 0;
    border: none;
    border-bottom: 1px solid var(--caos-color-accent);
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
    line-height: inherit;
    color: inherit;
  }
  :host([inline]) input:focus-visible,
  :host([inline]) input:focus {
    border-color: transparent;
    border-bottom-color: var(--caos-color-accent);
    background: transparent;
    box-shadow: none;
  }
  :host([inline]) input[aria-invalid='true'],
  :host([inline]) input[aria-invalid='true']:focus { border-bottom-color: var(--caos-color-danger); }
`);

/** Ids the group, box labels and refusals point at, unique per instance. */
let locationUid = 0;

export class CaosLocationField extends CaosElement {
  static get observedAttributes(): string[] {
    return [
      'label',
      'latitude',
      'longitude',
      'required',
      'disabled',
      'readonly',
      'invalid',
      'message',
      'latitude-message',
      'longitude-message',
    ];
  }

  private readonly labelText: HTMLSpanElement;
  private readonly reqEl: HTMLSpanElement;
  private readonly boxes = {} as Record<AxisName, HTMLInputElement>;
  private readonly notes = {} as Record<AxisName, HTMLDivElement>;
  private readonly messageEl: HTMLDivElement;

  /** Refusals this control raised itself, per box. Shown ahead of any the page set. */
  private refused: Record<AxisName, string> = { latitude: '', longitude: '' };
  /** The pair last committed or handed in. A commit fires `change` only when it differs. */
  private committed: CaosLocationValue | null = null;

  constructor() {
    super(sheet);
    const id = `caos-location-${++locationUid}`;

    const field = document.createElement('div');
    field.className = 'field';
    field.setAttribute('role', 'group');
    field.setAttribute('aria-labelledby', `${id}-label`);

    const heading = document.createElement('span');
    heading.className = 'label';
    heading.id = `${id}-label`;
    heading.setAttribute('part', 'label');
    this.labelText = document.createElement('span');
    this.labelText.className = 'label-text';
    this.reqEl = document.createElement('span');
    this.reqEl.className = 'req';
    this.reqEl.textContent = '*';
    this.reqEl.hidden = true;
    this.reqEl.setAttribute('aria-hidden', 'true');
    heading.append(this.labelText, this.reqEl);

    const pair = document.createElement('div');
    pair.className = 'pair';
    pair.setAttribute('part', 'control');

    for (const axis of AXES) {
      const coord = document.createElement('div');
      coord.className = 'coord';

      const box = document.createElement('input');
      box.type = 'text';
      box.inputMode = 'decimal';
      box.autocomplete = 'off';
      box.spellcheck = false;
      box.id = `${id}-${axis.name}`;
      box.setAttribute('part', axis.name);

      const label = document.createElement('label');
      label.className = 'sub-label';
      label.htmlFor = box.id;
      label.textContent = axis.label;

      const note = document.createElement('div');
      note.className = 'sub-message';
      note.id = `${id}-${axis.name}-message`;
      note.setAttribute('part', `${axis.name}-message`);
      box.setAttribute('aria-describedby', note.id);

      // Typing into a refused box re-judges it, so a fixed coordinate stops being called wrong at
      // once. A box that was not refused is left alone until it is committed or left.
      box.addEventListener('input', () => {
        if (!this.refused[axis.name]) return;
        this.refused[axis.name] = refusal(axis, box.value.trim());
        this.showNotes();
      });
      // Leaving a box judges that box — but an EMPTY one is not called missing yet: the person is
      // most likely on their way to fill it, and "both empty" is a legitimate "no place".
      box.addEventListener('change', () => {
        const text = box.value.trim();
        this.refused[axis.name] = text === '' ? '' : refusal(axis, text);
        this.showNotes();
      });
      box.addEventListener('paste', (e) => {
        const pasted = (e as ClipboardEvent).clipboardData?.getData('text/plain') ?? '';
        const split = splitLocationPair(pasted);
        // A read-only field keeps its place: the paste is left to the browser, which puts nothing in.
        if (!split || this.hasAttribute('readonly')) return;
        e.preventDefault();
        this.boxes.latitude.value = split[0];
        this.boxes.longitude.value = split[1];
        const reading = readLocation(split[0], split[1]);
        this.refused = { latitude: reading.latitude, longitude: reading.longitude };
        this.showNotes();
      });

      coord.append(label, box, note);
      pair.append(coord);
      this.boxes[axis.name] = box;
      this.notes[axis.name] = note;
    }

    this.messageEl = document.createElement('div');
    this.messageEl.className = 'message';
    this.messageEl.setAttribute('part', 'message');

    field.append(heading, pair, this.messageEl);
    this.root.append(field);

    // Focus moving from one box to the other is not leaving the control.
    this.root.addEventListener('focusout', (e) => {
      const next = (e as FocusEvent).relatedTarget;
      if (next instanceof Node && this.root.contains(next)) return;
      this.commit();
    });

    this.root.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key !== 'Enter') return;
      const reading = readLocation(this.boxes.latitude.value, this.boxes.longitude.value);
      if (reading.value !== undefined && sameLocation(reading.value, this.committed)) {
        // Nothing to commit. Enter is left to whoever is listening — the record page's inline
        // editor closes on it, exactly as it does for every other kind.
        return;
      }
      // A refused pair, or a real change: this control answers Enter itself, and nothing outside
      // sees it. A refusal must not close an inline editor, and a change is announced once, by
      // `change`, not a second time by a listener that also reads Enter.
      e.preventDefault();
      e.stopPropagation();
      this.commit();
    });
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(name: string, previous: string | null, next: string | null): void {
    if ((name === 'latitude' || name === 'longitude') && previous !== next) this.adopt();
    this.sync();
  }

  /**
   * The pair in the boxes; `null` when both are empty. While the boxes hold a refused pair this is
   * the last committed one, because a refused pair is not a value.
   */
  get value(): CaosLocationValue | null {
    const reading = readLocation(this.boxes.latitude.value, this.boxes.longitude.value);
    return reading.value === undefined ? this.committed : reading.value;
  }
  set value(v: CaosLocationValue | null) {
    if (v === null || v === undefined) {
      this.removeAttribute('latitude');
      this.removeAttribute('longitude');
    } else {
      this.setAttribute('latitude', String(v.latitude));
      this.setAttribute('longitude', String(v.longitude));
    }
    // Setting what is already there raises no attribute callback, and the boxes may hold typing.
    this.adopt();
  }

  /**
   * Whether a refusal this control found is showing under either box — the pair is held, not
   * committed. Whoever closes the control when focus leaves it reads this, so that leaving does not
   * quietly put the old place back and throw away what was typed along with the reason it was refused.
   */
  get holdsRefusal(): boolean {
    return Boolean(this.refused.latitude || this.refused.longitude);
  }

  /** Put the caret in the latitude box, with its text selected. */
  override focus(options?: FocusOptions): void {
    this.boxes.latitude.focus(options);
    this.boxes.latitude.select();
  }

  /** Take the `latitude` / `longitude` attributes as the committed pair, and show them in the boxes. */
  private adopt(): void {
    const lat = this.getAttribute('latitude') ?? '';
    const lon = this.getAttribute('longitude') ?? '';
    this.boxes.latitude.value = lat;
    this.boxes.longitude.value = lon;
    const reading = readLocation(lat, lon);
    this.committed = reading.value ?? null;
    this.refused = { latitude: '', longitude: '' };
  }

  /**
   * Judge the pair and, when it stands and differs from the last one committed, commit it and fire
   * `change`. A refused pair commits nothing and puts each reason under its box.
   */
  private commit(): void {
    // Read-only never commits. Its boxes take no typing, and nothing a script leaves in them is a change.
    if (this.hasAttribute('readonly')) return;
    const reading = readLocation(this.boxes.latitude.value, this.boxes.longitude.value);
    this.refused = { latitude: reading.latitude, longitude: reading.longitude };
    this.showNotes();
    if (reading.value === undefined || sameLocation(reading.value, this.committed)) return;
    this.value = reading.value;
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private showNotes(): void {
    const fieldInvalid = this.hasAttribute('invalid');
    for (const axis of AXES) {
      const note = this.refused[axis.name] || (this.getAttribute(`${axis.name}-message`) ?? '');
      this.notes[axis.name].textContent = note;
      const box = this.boxes[axis.name];
      if (note || fieldInvalid) box.setAttribute('aria-invalid', 'true');
      else box.removeAttribute('aria-invalid');
    }
  }

  private sync(): void {
    this.labelText.textContent = this.getAttribute('label') ?? '';
    const required = this.hasAttribute('required');
    this.reqEl.hidden = !required;
    const disabled = this.hasAttribute('disabled');
    const readonly = this.hasAttribute('readonly');
    for (const axis of AXES) {
      const box = this.boxes[axis.name];
      box.disabled = disabled;
      box.readOnly = readonly;
      box.required = required;
    }
    this.showNotes();
    this.messageEl.textContent = this.getAttribute('message') ?? '';
  }
}

define('caos-location-field', CaosLocationField);

declare global {
  interface HTMLElementTagNameMap {
    'caos-location-field': CaosLocationField;
  }
}
